import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Play, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { absolutizeUrl } from "@/components/ImagePicker";
import { useLang } from "@/context/LanguageContext";
import SEO from "@/components/SEO";
import PageHeader, { SectionMark } from "@/components/PageHeader";
import { useEvents } from "@/components/events/NextEvents";
import { fmtEventDates, pick, ytId as eventYtId } from "@/components/events/eventUtils";

/* Extract a YouTube videoId from any common YouTube URL */
function ytId(url) {
    if (!url) return null;
    const m = String(url).match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?(?:.*&)?v=|embed\/|shorts\/))([A-Za-z0-9_-]{6,})/);
    return m ? m[1] : null;
}
function ytThumb(id) {
    return id ? `https://img.youtube.com/vi/${id}/hqdefault.jpg` : null;
}

export default function Video() {
    const { t, lang } = useLang();
    const [trails, setTrails] = useState([]);
    const [activeId, setActiveId] = useState(null);
    const events = useEvents();
    const eventVideos = (events || []).filter((ev) => eventYtId(ev.video_url));

    useEffect(() => {
        api.get("/trails")
            .then((r) => setTrails(r.data || []))
            .catch(() => setTrails([]));
    }, []);

    const videos = useMemo(() => trails
        .map((tr) => {
            const id = ytId(tr.youtube_url);
            return id ? {
                id, trail: tr, vid: id,
                title: (lang === "en" && tr.name_en) ? tr.name_en : tr.name,
                summary: (lang === "en" && tr.summary_en) ? tr.summary_en : tr.summary,
            } : null;
        })
        .filter(Boolean), [trails, lang]);

    return (
        <div data-testid="video-page" className="bg-abnormal-ink">
            <SEO
                title={lang === "en" ? "MTB Argentario · Videos" : "Video MTB Argentario"}
                description={lang === "en"
                    ? "POV, drone and technical-line videos from Monte Argentario's MTB Enduro trails."
                    : "POV, riprese drone e passaggi tecnici dai trail MTB Enduro del Monte Argentario."}
                path="/video"
            />
            <PageHeader
                label="TRAILS · VIDEO"
                title={lang === "en" ? "See the lines" : "Guarda le linee"}
                accent={lang === "en" ? "before you ride" : "prima di girare"}
                tagline={["POV", "Drone", lang === "en" ? "Technical passes" : "Passaggi tecnici"]}
                sub={lang === "en"
                    ? "POV, drone and technical passes from every trail. Tap to watch — autoplay starts only when you press play."
                    : "POV, riprese drone, passaggi tecnici. Tocca per riprodurre — autoplay solo dopo il tuo click."}
            />
            <div className="max-w-[1400px] mx-auto px-5 md:px-10 pb-14 md:pb-20">

            {videos.length === 0 ? (
                <div className="mt-16 py-20 text-center text-abnormal-paperDim label-mono border border-white/10">
                    {lang === "en" ? "No videos yet. Check back soon." : "Nessun video ancora. Torna presto."}
                </div>
            ) : (
                <div data-testid="video-grid"
                    className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
                    {videos.map((v) => {
                        const isActive = activeId === v.vid;
                        return (
                            <article key={v.id} data-testid={`video-card-${v.trail.slug}`}
                                className="group bg-abnormal-charcoal border border-white/10 hover:border-abnormal-acid transition-colors overflow-hidden flex flex-col">
                                <div className="relative aspect-video bg-black overflow-hidden">
                                    {isActive ? (
                                        <iframe
                                            src={`https://www.youtube.com/embed/${v.vid}?autoplay=1&rel=0`}
                                            title={v.title}
                                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                            allowFullScreen
                                            className="absolute inset-0 w-full h-full"
                                        />
                                    ) : (
                                        <button onClick={() => setActiveId(v.vid)}
                                            data-testid={`video-play-${v.trail.slug}`}
                                            className="block w-full h-full relative cursor-pointer">
                                            <img src={ytThumb(v.vid)} alt={v.title} loading="lazy"
                                                className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                                            <span className="absolute inset-0 bg-black/30 group-hover:bg-black/10 transition-colors" />
                                            <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-16 h-16 bg-abnormal-acid text-black flex items-center justify-center group-hover:scale-110 transition-transform">
                                                <Play size={26} className="ml-1" />
                                            </span>
                                        </button>
                                    )}
                                </div>
                                <div className="p-5 flex-1 flex flex-col">
                                    <p className="label-mono text-abnormal-paperDim/60">{t(`trailMeta.difficulty.${v.trail.difficulty}`) || v.trail.difficulty} · {v.trail.length_km} km</p>
                                    <h3 className="font-display text-2xl md:text-3xl uppercase leading-tight mt-2 text-abnormal-paper">{v.title}<span className="text-abnormal-acid">.</span></h3>
                                    <p className="text-abnormal-paperDim text-sm mt-2 flex-1 line-clamp-2">{v.summary}</p>
                                    <Link to={`/trail/${v.trail.slug}`} data-testid={`video-trail-link-${v.trail.slug}`}
                                        className="mt-4 inline-flex items-center gap-2 label-mono font-bold text-abnormal-acid hover:gap-3 transition-all">
                                        {lang === "en" ? "Open trail" : "Apri il trail"} <ArrowRight size={11} />
                                    </Link>
                                </div>
                            </article>
                        );
                    })}
                </div>
            )}

            {eventVideos.length > 0 && (
                <section data-testid="event-videos" className="mt-16">
                    <SectionMark n="02" title={lang === "en" ? "Event videos · Races & camps" : "Video eventi · Gare & corsi"}
                        right={<Link to="/eventi" className="label-mono text-abnormal-acid hover:underline underline-offset-4 inline-flex items-center gap-1">{lang === "en" ? "All events" : "Tutti gli eventi"} <ArrowRight size={12} /></Link>} />
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
                        {eventVideos.map((ev) => (
                            <Link key={ev.slug} to={`/eventi/${ev.slug}`} data-testid={`event-video-card-${ev.slug}`}
                                className="group bg-abnormal-charcoal border border-white/10 hover:border-abnormal-acid transition-colors overflow-hidden flex flex-col">
                                <div className="relative aspect-video bg-black overflow-hidden">
                                    <img src={ytThumb(eventYtId(ev.video_url))} alt={pick(ev, "title", lang)} loading="lazy" className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                                    <span className="absolute inset-0 bg-black/30 group-hover:bg-black/10 transition-colors" />
                                    <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-16 h-16 bg-abnormal-acid text-black flex items-center justify-center group-hover:scale-110 transition-transform"><Play size={24} fill="currentColor" /></span>
                                    <span className="absolute top-3 left-3 label-mono bg-black/85 text-abnormal-paper border border-white/20 px-2 py-1">{lang === "en" ? "EVENT" : "EVENTO"}</span>
                                </div>
                                <div className="p-4">
                                    <p className="label-mono text-abnormal-acid">{fmtEventDates(ev, lang)} · {ev.location}</p>
                                    <h3 className="mt-1 font-display uppercase text-2xl leading-none text-abnormal-paper group-hover:text-abnormal-acid transition-colors">{pick(ev, "tagline", lang) || pick(ev, "title", lang)}</h3>
                                    <p className="mt-2 label-mono text-abnormal-paperDim inline-flex items-center gap-1">{lang === "en" ? "Watch on the event page" : "Guarda nella pagina evento"} <ArrowRight size={11} /></p>
                                </div>
                            </Link>
                        ))}
                    </div>
                </section>
            )}
            </div>
        </div>
    );
}
